import React, { useCallback, useImperativeHandle, useRef, useState } from "react";
import classNames from "classnames";
import mergeRefs from "merge-refs";
import { DictType, ScenarioAutocompletes } from "@/model";
import { triggerInputChangeEvent } from "@/util/inputUtil";
import InputAutocompleteMenu from "@/component/input/InputAutocompleteMenu";


export type TextInputRef = HTMLInputElement & { set: (value: string) => void };

export type TextInputProps = React.ComponentProps<"input"> & {
    invalid?: boolean;
    autocompletes?: DictType<ScenarioAutocompletes>;
    useDirty?: boolean;
}

const TextInput = React.forwardRef<TextInputRef | HTMLInputElement, TextInputProps>((props, propsRef) => {
    const {
        required,
        maxLength,
        autocompletes,
        className: propsClassName,
        invalid: propsInvalid,
        onChange: propsOnChange,
        defaultValue,
        value: propsValue,
        useDirty = false,
        ...rest
    } = props;

    const inputRef = useRef<HTMLInputElement>(null);
    const [autocompleteTargetText, setAutocompleteTargetText] = useState<string>(defaultValue as string ?? propsValue as string ?? "");

    const isDirtyRef = useRef<boolean>(false);
    const validateRequired = useCallback((value: string) => (!!required && !value.trim() && (useDirty ? isDirtyRef.current : true)), [required, useDirty]);
    const [isInvalidRequired, setIsInvalidRequired] = useState<boolean>(validateRequired(defaultValue as string ?? propsValue as string ?? ""));

    const onSelectAutocomplete = (value: string) => {
        if (!inputRef.current) {
            return;
        }
        triggerInputChangeEvent(inputRef.current, value);
    };

    const onChange = (e: React.ChangeEvent<HTMLInputElement>) => {
        isDirtyRef.current = true;
        setAutocompleteTargetText(e.target.value);
        setIsInvalidRequired(validateRequired(e.target.value));
        propsOnChange?.(e);
    };

    useImperativeHandle(propsRef, () => {
        if (!inputRef.current) {
            return null as unknown as TextInputRef;
        }
        (inputRef.current as TextInputRef).set = (value: string) => {
            if (!inputRef.current) {
                return;
            }
            inputRef.current.value = value;
            setAutocompleteTargetText(value);
            setIsInvalidRequired(validateRequired(value));
        };
        return inputRef.current;
    }, [validateRequired]);

    return (<>
        <input {...rest}
               ref={mergeRefs(inputRef, propsRef)}
               type="text"
               aria-invalid={(!rest.disabled && (propsInvalid || isInvalidRequired)) ? true : undefined}
               className={classNames(
                   "input-default",
                   (!rest.disabled && (propsInvalid || isInvalidRequired)) && "input-invalid",
                   propsClassName
               )}
               value={propsValue}
               defaultValue={defaultValue}
               onChange={onChange}
        />
        {autocompletes && (
            <InputAutocompleteMenu targetRef={inputRef}
                                   targetText={autocompleteTargetText}
                                   autocompletes={autocompletes}
                                   onSelect={onSelectAutocomplete}
            />
        )}
    </>);
});

export default TextInput;
